/**
 * SDK client instance that attaches the active auth mode's bearer token. Uses
 * modelPropertyNaming=original so all fields preserve backend snake_case.
 */

import { Configuration, DefaultApi, ResponseError } from "@/generated/sdk"
import { getApiToken } from "./apiToken"

const basePath = typeof window === "undefined" ? "http://localhost" : ""

/**
 * Error for non-2xx SDK responses; exposes HTTP `status` for branching.
 */
export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = "ApiError"
    this.status = status
  }
}

export const sdk = new DefaultApi(
  new Configuration({
    basePath,
    middleware: [
      {
        async pre(context) {
          try {
            const token = await getApiToken()
            if (token) {
              const headers = new Headers(context.init.headers)
              headers.set("Authorization", `Bearer ${token}`)
              context.init.headers = headers
            }
          } catch {
            // Token retrieval failed — proceed without auth (will get 401)
          }
          return context
        },
      },
    ],
  }),
)

/**
 * Call a raw SDK method, normalize errors, and parse JSON response.
 */
export async function sdkRaw<T>(promise: Promise<{ raw: Response }>): Promise<T> {
  try {
    const response = await promise
    return response.raw.json()
  } catch (err) {
    if (err instanceof ResponseError) {
      let message = `API Error: ${err.response.status}`
      try {
        const body = await err.response.json()
        if (body.error) message = body.error
        else if (body.message) message = body.message
      } catch {
        // non-JSON error body
      }
      throw new ApiError(message, err.response.status)
    }
    throw err
  }
}

/**
 * Call a typed SDK method and normalize errors.
 *
 * Non-2xx responses are re-thrown as `Error` instances whose message includes the raw JSON error
 * body appended after `" - "`. Callers can parse the tail to recover structured fields (e.g.
 * `missing_fields` from publish_validation.py). See `WorkflowActions.parseApiError` for an example
 * consumer.
 */
export async function sdkCall<T>(promise: Promise<T>): Promise<T> {
  try {
    return await promise
  } catch (err) {
    if (err instanceof ResponseError) {
      let message = `API Error: ${err.response.status}`
      try {
        const body = await err.response.json()
        const topLine = body.error || body.message
        if (topLine) message = topLine
        // Preserve the full body as a JSON tail so callers can recover
        // structured error fields (missing_fields, validation_errors, ...).
        // The tail format matches parseApiError's regex: /- ({.+})$/
        if (typeof body === "object" && body !== null) {
          message = `${message} - ${JSON.stringify(body)}`
        }
      } catch {
        // non-JSON error body
      }
      throw new ApiError(message, err.response.status)
    }
    throw err
  }
}

/**
 * Authenticated GET for endpoints the generated SDK doesn't fully model (e.g. query params absent
 * from the OpenAPI spec). Attaches the mode-appropriate bearer token and parses the JSON response.
 * Prefer the typed `sdk` methods; use this only where the SDK can't express the request.
 */
export async function authorizedGet<T>(url: string): Promise<T> {
  const token = await getApiToken()
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    // Throw ApiError (matching sdkCall/sdkRaw) so the HTTP status survives for callers that branch
    // on it — e.g. a 403 on a forbidden pinned version.
    throw new ApiError(body.error || body.message || `API Error: ${res.status}`, res.status)
  }
  return res.json()
}
